import { useMemo } from 'react'
import { useExplorerConfigurations } from '../../../../api/coreData'

const CLIENT_LEVEL_TYPE_ID = 201

/**
 * Buckets the user's saved explorer configurations into three sections
 * relative to the current client context:
 *
 * - `thisClient`: views with a scope assignment matching
 *   (levelTypeId=201, levelId=clientId). Sorted ascending by `ordinal`
 *   (lowest = implicit default), tie-broken by `explorerConfigurationId`.
 *   Each entry is decorated with the resolved `ordinal` lifted out of the
 *   matching scope so consumers don't have to re-search.
 * - `myViews`: views with no scope assignments at all (legacy / global).
 * - `otherClients`: views with at least one scope assignment, but none for
 *   the current `clientId`. The dialog uses this to surface "this view is
 *   already in use elsewhere" without rendering names by default.
 *
 * Returns the underlying React Query state (`isLoading`, `error`, etc.)
 * merged with the three derived arrays so the caller can render loading
 * states uniformly. When `clientId` is null/undefined every saved view
 * with assignments falls into `otherClients` and `thisClient` is empty;
 * the caller is expected to gate UI on `clientId` separately.
 */
export function useExplorerConfigurationsForClient (clientId) {
  const query = useExplorerConfigurations()
  const { data: configs } = query

  const buckets = useMemo(() => {
    const thisClient = []
    const myViews = []
    const otherClients = []

    for (const config of configs || []) {
      const scopes = config.scopes || []
      if (scopes.length === 0) {
        myViews.push(config)
        continue
      }
      const here = clientId != null
        ? scopes.find(s => s.levelTypeId === CLIENT_LEVEL_TYPE_ID && s.levelId === clientId)
        : null
      if (here) {
        thisClient.push({ ...config, ordinal: here.ordinal })
      } else {
        otherClients.push(config)
      }
    }

    thisClient.sort((a, b) => (a.ordinal - b.ordinal) || (a.explorerConfigurationId - b.explorerConfigurationId))

    return { thisClient, myViews, otherClients }
  }, [configs, clientId])

  return {
    ...query,
    ...buckets
  }
}
